import { EnrollmentStatus } from "./enrollmentItem";
import { Address } from "@portal/app/_common/types/address";
import type { PreLeadRedirectCompositeParam } from "@portal/app/_common/lib/preLeadRedirectComposite";

export type { PreLeadRedirectCompositeParam };

export const PROGRAM_ENVIRONMENT = ["IN_PERSON", "HYBRID", "ONLINE"] as const;
export type ProgramEnvironment = (typeof PROGRAM_ENVIRONMENT)[number];

export type RevenueConfiguration = {
  [key in EnrollmentStatus]?: {
    percent: number;
    dollars: number;
  };
};

export const COHORT_DESIGNATION = {
  REQUIRED: "REQUIRED",
  ASYNC: "ASYNC",
  UNKNOWN: "UNKNOWN",
} as const;

export type CohortDesignation = (typeof COHORT_DESIGNATION)[keyof typeof COHORT_DESIGNATION];

export const GEO_RESTRICTION = {
  REGION: "REGION",
  NONE: "NONE",
} as const;

export type GeoRestriction = (typeof GEO_RESTRICTION)[keyof typeof GEO_RESTRICTION];

export type GeoRestrictionRegion = {
  state: string[];
};

export const CUSTOMER_FACING_PROGRAM_TAGS = {
  TOP_RATED: "TOP_RATED",
  STUDENT_FAVORITE: "STUDENT_FAVORITE",
  ACCELERATED: "ACCELERATED",
  FLEXIBLE_SCHEDULE: "FLEXIBLE_SCHEDULE",
  SELF_PACED: "SELF_PACED",
  BUDGET_FRIENDLY: "BUDGET_FRIENDLY",
  QUICK_COMMUNICATION: "QUICK_COMMUNICATION",
  NONPROFIT: "NONPROFIT",
  CAN_TRANSFER_CREDITS: "CAN_TRANSFER_CREDITS",
  DEGREE_COMPLETION: "DEGREE_COMPLETION",
  CHRISTIAN_UNIVERSITY: "CHRISTIAN_UNIVERSITY",
} as const;

/**
 * This is an ordered / ranked list of program tags. This controls the order in
 * which tags are displayed in dream search.
 */
export const CUSTOMER_FACING_PROGRAM_TAGS_PRIORITY: (keyof typeof CUSTOMER_FACING_PROGRAM_TAGS)[] =
  [
    "TOP_RATED",
    "STUDENT_FAVORITE",
    "ACCELERATED",
    "FLEXIBLE_SCHEDULE",
    "SELF_PACED",
    "BUDGET_FRIENDLY",
    "QUICK_COMMUNICATION",
    "CAN_TRANSFER_CREDITS",
    "DEGREE_COMPLETION",
    "NONPROFIT",
    "CHRISTIAN_UNIVERSITY",
  ] as const;

export const PROGRAM_TAG = {
  ACCREDITED: "ACCREDITED",
  NOT_ACCREDITED: "NOT_ACCREDITED",
  ...CUSTOMER_FACING_PROGRAM_TAGS,
} as const;

export type ProgramTag = (typeof PROGRAM_TAG)[keyof typeof PROGRAM_TAG];
export type CustomerFacingProgramTag = (typeof CUSTOMER_FACING_PROGRAM_TAGS_PRIORITY)[number];

export const PROGRAM_LEVEL = {
  CERTIFICATE: "CERTIFICATE",
  ASSOCIATE: "ASSOCIATE",
  BACHELOR: "BACHELOR",
  GRADUATE: "GRADUATE",
} as const;

export type ProgramLevel = (typeof PROGRAM_LEVEL)[keyof typeof PROGRAM_LEVEL];

/**
 * Which acquisition channel a Program record serves. We keep a separate copy of
 * a program per channel because caps, cost-per-lead and revenue configuration
 * differ between them, and campaigns are scoped against one set or the other.
 *
 * The field is nullable everywhere: a program with no channel is *not yet
 * categorized*, which is a distinct state from either value here. Treat it as
 * three-state — Marketplace, Social, uncategorized — rather than defaulting.
 */
export const PROGRAM_CHANNEL = {
  MARKETPLACE: "MARKETPLACE",
  SOCIAL: "SOCIAL",
} as const;

export type ProgramChannel = (typeof PROGRAM_CHANNEL)[keyof typeof PROGRAM_CHANNEL];

export type DynamicFieldsAnswerType = {
  GRADUATED_HIGH_SCHOOL_IN: string;
  HAS_MILITARY_AFFILIATION: boolean;
  HAS_RN_LICENSE: string;
  IS_US_CITIZEN: boolean;
  NAME_FIRST: string;
  NAME_LAST: string;
  EMAIL: string;
  PHONE: string;
  ADDRESS_FULL: Address;
  ADDRESS_ZIP: Address;
};
export const PROGRAM_REQUIRED_FIELD = {
  GRADUATED_HIGH_SCHOOL_IN: "GRADUATED_HIGH_SCHOOL_IN",
  HAS_MILITARY_AFFILIATION: "HAS_MILITARY_AFFILIATION",
  HAS_RN_LICENSE: "HAS_RN_LICENSE",
  IS_US_CITIZEN: "IS_US_CITIZEN",
  NAME_FIRST: "NAME_FIRST",
  NAME_LAST: "NAME_LAST",
  EMAIL: "EMAIL",
  PHONE: "PHONE",
  ADDRESS_FULL: "ADDRESS_FULL",
  ADDRESS_ZIP: "ADDRESS_ZIP",
} as const;
export const DEFAULT_HS_GRAD_YEAR_MIN = 1970;

export const DEFAULT_PROGRAM_REQUIRED_FIELDS: ProgramRequiredField[] = [
  "NAME_FIRST",
  "NAME_LAST",
  "EMAIL",
  "PHONE",
  "ADDRESS_FULL",
] as const;
export type ProgramRequiredField =
  (typeof PROGRAM_REQUIRED_FIELD)[keyof typeof PROGRAM_REQUIRED_FIELD];
export const programRequiredFieldEnumMap: Record<ProgramRequiredField, string> = {
  ADDRESS_FULL: "Full Address",
  ADDRESS_ZIP: "Only Zip Code",
  EMAIL: "Email",
  GRADUATED_HIGH_SCHOOL_IN: "Graduated High School In",
  HAS_MILITARY_AFFILIATION: "Has Military Affiliation",
  HAS_RN_LICENSE: "Has RN License",
  IS_US_CITIZEN: "Is US Citizen",
  NAME_FIRST: "First Name",
  NAME_LAST: "Last Name",
  PHONE: "Phone",
};

export const ELIGIBILITY_REQUIREMENT = {
  NEEDS_HIGH_SCHOOL_DIPLOMA: "NEEDS_HIGH_SCHOOL_DIPLOMA",
} as const;

export type EligibilityRequirement =
  (typeof ELIGIBILITY_REQUIREMENT)[keyof typeof ELIGIBILITY_REQUIREMENT];

export const DOB_FIELD_VISIBILITY = {
  REQUIRED: "REQUIRED",
  OPTIONAL: "OPTIONAL",
  HIDDEN: "HIDDEN",
} as const;

export type DobFieldVisibility =
  (typeof DOB_FIELD_VISIBILITY)[keyof typeof DOB_FIELD_VISIBILITY];

export type ProgramMetadata = {
  customCtaCopy?: string;
  customDisclaimer?: string;
  leadRedirect?: string;
  notes?: string;
  preLeadRedirect?: string;
  preLeadRedirectParams?: {
    marketplaceEventId?: string;
    customerId?: string;
    everflowTransactionId?: string;
    programSlug?: string;
    userZip?: string;
  };
  /**
   * Bundles several IDs into single query params (e.g. UMA's `vendorleadid`).
   * Coexists with `preLeadRedirectParams`. See `preLeadRedirectComposite.ts`.
   */
  preLeadRedirectCompositeParams?: PreLeadRedirectCompositeParam[];
  /**
   * When true, suppresses the simple per-ID redirect params (default m/c/e/s/z
   * and any custom renames) so only composite params are sent. For
   * composite-only partners like UMA. Composite params are unaffected.
   */
  preLeadRedirectSuppressDefaultParams?: boolean;
  preLeadRedirectInIframe?: boolean;
  shouldHideProgramDescriptors?: boolean;
  allowHybridSignup?: boolean;
};
